import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientPortalLayout from "./ClientPortalLayout";
import { Container, Grid, Paper, Typography, Button, Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, List, ListItem, ListItemText, ListItemIcon, Divider, Avatar, Badge, LinearProgress, Alert, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Folder as FolderIcon, CloudDownload as DownloadIcon, Send as SendIcon, AttachFile as AttachFileIcon, Payment as PaymentIcon, Receipt as ReceiptIcon, Gavel as GavelIcon, Schedule as ScheduleIcon, Lock as LockIcon, LockOpen as LockOpenIcon, Visibility as VisibilityIcon, Message as MessageIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon, Info as InfoIcon } from "@mui/icons-material";
import { useSnackbar } from "@codejq/react-simple-snackbar";
import { useDispatch } from "react-redux";
import { getClientPortalData } from "../../actions/clientPortalActions";
import * as api from "../../api";
import { format } from "date-fns";
import { SNACKBAR_DURATION_MS, getSnackbarColor } from "../../utils/snackbar";
const ClientPortal = () => {
  const {
    t,
    i18n
  } = useTranslation("crm");
  const history = useHistory();
  const [openSnackbar] = useSnackbar();
  const dispatch = useDispatch();

  // Helper function to normalize language code (e.g., en-US -> en)
  const normalizeLanguage = lang => {
    if (!lang) return 'en';
    return lang.split('-')[0]; // Get only the base language code
  };
  const currentLang = normalizeLanguage(i18n.language);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [cases, setCases] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [messageData, setMessageData] = useState({
    subject: "",
    message: "",
    attachments: []
  });
  const [paymentData, setPaymentData] = useState({
    invoiceId: "",
    amount: "",
    paymentMethod: "credit_card",
    cardNumber: "",
    cardExpiry: "",
    cardCVV: ""
  });

  // Fetch client portal data using Redux
  const fetchClientData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dispatch(getClientPortalData());
      setClientInfo(data.data?.client || {});
      setCases(data.data?.cases || []);
      setDocuments(data.data?.documents || []);
      setMessages(data.data?.messages || []);
      setInvoices(data.data?.invoices || []);
      setAppointments(data.data?.appointments || []);
    } catch (error) {
      console.error("Error fetching client data:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);
  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);
  const handleDownloadDocument = async documentId => {
    try {
      const {
        data
      } = await api.downloadClientDocument(documentId);
      // Trigger download
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `document_${documentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      openSnackbar("Document downloaded successfully", SNACKBAR_DURATION_MS, getSnackbarColor("Document downloaded successfully"));
    } catch (error) {
      console.error("Error downloading document:", error);
      openSnackbar("Error downloading document", SNACKBAR_DURATION_MS, getSnackbarColor("Error downloading document"));
    }
  };
  const handleSendMessage = async () => {
    try {
      await api.sendClientMessage(messageData);
      openSnackbar("Message sent successfully", SNACKBAR_DURATION_MS, getSnackbarColor("Message sent successfully"));
      setOpenMessageDialog(false);
      setMessageData({
        subject: "",
        message: "",
        attachments: []
      });
      fetchClientData();
    } catch (error) {
      console.error("Error sending message:", error);
      openSnackbar("Error sending message", SNACKBAR_DURATION_MS, getSnackbarColor("Error sending message"));
    }
  };
  const handleMakePayment = async () => {
    try {
      await api.makeClientPayment(paymentData);
      openSnackbar("Payment processed successfully", SNACKBAR_DURATION_MS, getSnackbarColor("Payment processed successfully"));
      setOpenPaymentDialog(false);
      setPaymentData({
        invoiceId: "",
        amount: "",
        paymentMethod: "credit_card",
        cardNumber: "",
        cardExpiry: "",
        cardCVV: ""
      });
      fetchClientData();
    } catch (error) {
      console.error("Error processing payment:", error);
      openSnackbar("Error processing payment", SNACKBAR_DURATION_MS, getSnackbarColor("Error processing payment"));
    }
  };
  const getCaseStatusColor = status => {
    const colors = {
      active: "primary",
      pending: "warning",
      closed: "default",
      on_hold: "info"
    };
    return colors[status] || "default";
  };
  const getInvoiceStatusColor = status => {
    const colors = {
      paid: "success",
      unpaid: "error",
      partial: "warning",
      overdue: "error"
    };
    return colors[status] || "default";
  };

  // Overview Tab
  const renderOverviewTab = () => <Grid container spacing={3}>
      {/* Client Info Card */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{
              width: 60,
              height: 60,
              mr: 2,
              bgcolor: "primary.main"
            }}>
                {clientInfo?.name?.charAt(0) || "C"}
              </Avatar>
              <Box>
                <Typography variant="h6">{clientInfo?.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('client_id')}: {clientInfo?.clientId}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{
            my: 2
          }} />
            <Typography variant="body2" gutterBottom>
              <strong>{t('email')}:</strong> {clientInfo?.email}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>{t('phone')}:</strong> {clientInfo?.phone}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>{t('member_since')}:</strong>{" "}
              {clientInfo?.createdAt ? format(new Date(clientInfo.createdAt), "MMM dd, yyyy") : t("N/A")}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Stats */}
      <Grid item xs={12} md={8}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4">{cases.length}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('active_cases')}
                    </Typography>
                  </Box>
                  <GavelIcon color="primary" sx={{
                  fontSize: 40
                }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4">{documents.length}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('documents')}
                    </Typography>
                  </Box>
                  <FolderIcon color="info" sx={{
                  fontSize: 40
                }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4">
                      {messages.filter(m => !m.read).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('unread_messages')}
                    </Typography>
                  </Box>
                  <MessageIcon color="warning" sx={{
                  fontSize: 40
                }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4">
                      $
                      {invoices.filter(inv => inv.status === "unpaid").reduce((sum, inv) => sum + inv.amount, 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('outstanding')}
                    </Typography>
                  </Box>
                  <PaymentIcon color="error" sx={{
                  fontSize: 40
                }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Card sx={{
        mt: 2
      }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('recent_activity')}
            </Typography>
            <List>
              {appointments.slice(0, 3).map((appointment, index) => <React.Fragment key={appointment._id}>
                  <ListItem>
                    <ListItemIcon>
                      <ScheduleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={appointment.title} secondary={`Scheduled for ${format(new Date(appointment.date), "MMM dd, yyyy - hh:mm a")}`} />
                  </ListItem>
                  {index < 2 && <Divider />}
                </React.Fragment>)}
              {appointments.length === 0 && <ListItem>
                  <ListItemText secondary={t('no_recent_appointments')} />
                </ListItem>}
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>;

  // Cases Tab
  const renderCasesTab = () => <Paper>
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          {t('my_cases')}
        </Typography>
      </Box>
      <TableContainer>
        <Table className="stripedTable">
          <TableHead>
            <TableRow>
              <TableCell>{t('case_number')}</TableCell>
              <TableCell>{t('case_title')}</TableCell>
              <TableCell>{t('case_type')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('next_hearing')}</TableCell>
              <TableCell>{t('attorney')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cases.map(caseItem => <TableRow key={caseItem._id}>
                <TableCell>{caseItem.caseNumber}</TableCell>
                <TableCell>{caseItem.title}</TableCell>
                <TableCell>{caseItem.caseType}</TableCell>
                <TableCell>
                  <Chip label={caseItem.status} color={getCaseStatusColor(caseItem.status)} size="small" />
                </TableCell>
                <TableCell>
                  {caseItem.nextHearing ? format(new Date(caseItem.nextHearing), "MMM dd, yyyy") : t('not_scheduled')}
                </TableCell>
                <TableCell>{caseItem.attorney?.name || "N/A"}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => history.push(`/${currentLang}/app/law-firm/cases/${caseItem._id}`)}>
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>)}
            {cases.length === 0 && <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {t('no_cases_found')}
                  </Typography>
                </TableCell>
              </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>;

  // Documents Tab
  const renderDocumentsTab = () => <Paper>
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          {t('my_documents')}
        </Typography>
      </Box>
      <TableContainer>
        <Table className="stripedTable">
          <TableHead>
            <TableRow>
              <TableCell>{t('document_name')}</TableCell>
              <TableCell>{t('case')}</TableCell>
              <TableCell>{t('type')}</TableCell>
              <TableCell>{t('upload_date')}</TableCell>
              <TableCell>{t('access_level')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map(doc => <TableRow key={doc._id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <FolderIcon sx={{
                  mr: 1
                }} color="primary" />
                    {doc.name}
                  </Box>
                </TableCell>
                <TableCell>{doc.case?.caseNumber || t('general')}</TableCell>
                <TableCell>{doc.type}</TableCell>
                <TableCell>
                  {format(new Date(doc.uploadDate), "MMM dd, yyyy")}
                </TableCell>
                <TableCell>
                  {doc.accessLevel === "private" ? <Chip icon={<LockIcon />} label={t('private')} size="small" color="error" /> : <Chip icon={<LockOpenIcon />} label={t('shared')} size="small" color="success" />}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleDownloadDocument(doc._id)}>
                    <DownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>)}
            {documents.length === 0 && <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {t('no_documents_available_alt')}
                  </Typography>
                </TableCell>
              </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>;

  // Messages Tab
  const renderMessagesTab = () => <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{t('messages')}</Typography>
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => setOpenMessageDialog(true)}>
            {t('new_message')}
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <List>
            {messages.map((message, index) => <React.Fragment key={message._id}>
                <ListItem sx={{
              bgcolor: !message.read ? "action.hover" : "transparent",
              "&:hover": {
                bgcolor: "action.selected"
              }
            }}>
                  <ListItemIcon>
                    <Badge color="error" variant="dot" invisible={message.read}>
                      <MessageIcon color={!message.read ? "primary" : "disabled"} />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary={<Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight={!message.read ? "bold" : "normal"}>
                          {message.subject}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {format(new Date(message.date), "MMM dd, yyyy")}
                        </Typography>
                      </Box>} secondary={<>
                        <Typography variant="body2" color="textSecondary">
                          {t('from')}: {message.from}
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {message.preview}
                        </Typography>
                      </>} />
                </ListItem>
                {index < messages.length - 1 && <Divider />}
              </React.Fragment>)}
            {messages.length === 0 && <ListItem>
                <ListItemText primary={t('no_messages')} secondary={t('no_messages_yet')} />
              </ListItem>}
          </List>
        </Paper>
      </Grid>
    </Grid>;

  // Billing Tab
  const renderBillingTab = () => <Paper>
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          {t('invoices_payments')}
        </Typography>
      </Box>
      <TableContainer>
        <Table className="stripedTable">
          <TableHead>
            <TableRow>
              <TableCell>{t('invoice')}</TableCell>
              <TableCell>{t('case')}</TableCell>
              <TableCell>{t('issue_date')}</TableCell>
              <TableCell>{t('due_date')}</TableCell>
              <TableCell>{t('amount')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map(invoice => <TableRow key={invoice._id}>
                <TableCell>{invoice.invoiceNumber}</TableCell>
                <TableCell>{invoice.case?.caseNumber || t('general')}</TableCell>
                <TableCell>
                  {format(new Date(invoice.issueDate), "MMM dd, yyyy")}
                </TableCell>
                <TableCell>
                  {format(new Date(invoice.dueDate), "MMM dd, yyyy")}
                </TableCell>
                <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip label={invoice.status} color={getInvoiceStatusColor(invoice.status)} size="small" />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleDownloadDocument(invoice._id)}>
                    <ReceiptIcon />
                  </IconButton>
                  {(invoice.status === "unpaid" || invoice.status === "partial") && <Button size="small" variant="contained" color="primary" startIcon={<PaymentIcon />} onClick={() => {
                setSelectedInvoice(invoice);
                setPaymentData({
                  ...paymentData,
                  invoiceId: invoice._id,
                  amount: invoice.amount
                });
                setOpenPaymentDialog(true);
              }} sx={{
                ml: 1
              }}>
                      {t('pay')}
                    </Button>}
                </TableCell>
              </TableRow>)}
            {invoices.length === 0 && <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {t('no_invoices_found')}
                  </Typography>
                </TableCell>
              </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>;

  // New Message Dialog
  const renderMessageDialog = () => <Dialog open={openMessageDialog} onClose={() => setOpenMessageDialog(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('new_message')}</DialogTitle>
      <DialogContent>
        <TextField fullWidth label={t('subject')} value={messageData.subject} onChange={e => setMessageData({
        ...messageData,
        subject: e.target.value
      })} margin="normal" />
        <TextField fullWidth label={t('message')} value={messageData.message} onChange={e => setMessageData({
        ...messageData,
        message: e.target.value
      })} margin="normal" multiline rows={6} />
        <Button variant="outlined" startIcon={<AttachFileIcon />} sx={{
        mt: 2
      }}>
          {t('attach_file')}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenMessageDialog(false)}>{t('cancel')}</Button>
        <Button onClick={handleSendMessage} variant="contained" startIcon={<SendIcon />}>
          {t('send_message')}
        </Button>
      </DialogActions>
    </Dialog>;

  // Payment Dialog
  const renderPaymentDialog = () => <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('make_payment')}</DialogTitle>
      <DialogContent>
        {selectedInvoice && <Alert severity="info" sx={{
        mb: 2
      }}>
            {t('invoice')} {selectedInvoice.invoiceNumber} - {t('amount_due')}: $
            {selectedInvoice.amount.toFixed(2)}
          </Alert>}

        <TextField fullWidth label={t('amount')} type="number" value={paymentData.amount} onChange={e => setPaymentData({
        ...paymentData,
        amount: e.target.value
      })} margin="normal" />

        <FormControl fullWidth margin="normal">
          <InputLabel>{t('payment_method')}</InputLabel>
          <Select value={paymentData.paymentMethod} onChange={e => setPaymentData({
          ...paymentData,
          paymentMethod: e.target.value
        })} label={t('payment_method')}>
            <MenuItem value="credit_card">{t('credit_card')}</MenuItem>
            <MenuItem value="debit_card">{t('debit_card')}</MenuItem>
            <MenuItem value="bank_transfer">{t('bank_transfer')}</MenuItem>
          </Select>
        </FormControl>

        {(paymentData.paymentMethod === "credit_card" || paymentData.paymentMethod === "debit_card") && <>
            <TextField fullWidth label={t('card_number')} value={paymentData.cardNumber} onChange={e => setPaymentData({
          ...paymentData,
          cardNumber: e.target.value
        })} margin="normal" placeholder="1234 5678 9012 3456" />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label={t('expiry')} value={paymentData.cardExpiry} onChange={e => setPaymentData({
              ...paymentData,
              cardExpiry: e.target.value
            })} margin="normal" placeholder={t("MM/YY")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={t('cvv')} value={paymentData.cardCVV} onChange={e => setPaymentData({
              ...paymentData,
              cardCVV: e.target.value
            })} margin="normal" placeholder="123" />
              </Grid>
            </Grid>
          </>}

        <Alert severity="warning" sx={{
        mt: 2
      }}>
          <LockIcon fontSize="small" sx={{
          mr: 1,
          verticalAlign: "middle"
        }} />
          {t('payment_secure_message')}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenPaymentDialog(false)}>{t('cancel')}</Button>
        <Button onClick={handleMakePayment} variant="contained" color="primary" startIcon={<PaymentIcon />}>
          {t('process_payment')}
        </Button>
      </DialogActions>
    </Dialog>;

  // Build modules array for layout
  const modules = [{
    name: 'lawFirm',
    label: 'Legal Services',
    icon: <GavelIcon />,
    path: '/client-portal/law-firm'
  }];
  if (loading && !clientInfo) {
    return <ClientPortalLayout modules={modules}>
        <Box sx={{
        width: "100%",
        mt: 4
      }}>
          <LinearProgress />
          <Typography align="center" sx={{
          mt: 2
        }}>
            {t('loading_portal')}
          </Typography>
        </Box>
      </ClientPortalLayout>;
  }
  return <ClientPortalLayout modules={modules} currentModule="lawFirm">
      <Box>
        <Box sx={{
        mb: 3
      }}>
          <Typography variant="h4" gutterBottom>
            {t('legal_services_portal')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('welcome_back_manage', {
            name: clientInfo?.name
          })}
          </Typography>
        </Box>

        <Paper sx={{
        mb: 3
      }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto">
            <Tab label={t('overview')} icon={<InfoIcon />} iconPosition="start" />
            <Tab label={t('my_cases_tab')} icon={<GavelIcon />} iconPosition="start" />
            <Tab label={t('documents_tab')} icon={<FolderIcon />} iconPosition="start" />
            <Tab label={t('messages_tab')} icon={<MessageIcon />} iconPosition="start" />
            <Tab label={t('billing_tab')} icon={<PaymentIcon />} iconPosition="start" />
          </Tabs>
        </Paper>

        <Box sx={{
        mt: 3
      }}>
          {activeTab === 0 && renderOverviewTab()}
          {activeTab === 1 && renderCasesTab()}
          {activeTab === 2 && renderDocumentsTab()}
          {activeTab === 3 && renderMessagesTab()}
          {activeTab === 4 && renderBillingTab()}
        </Box>

        {renderMessageDialog()}
        {renderPaymentDialog()}
      </Box>
    </ClientPortalLayout>;
};
export default ClientPortal;
